/**
 * Voucher codes: generate, share, track usage, deactivate.
 *
 * Replaces `renderVoucherManagement`, `switchVoucherTab`,
 * `openGenerateVoucherModal`, `generateVoucherCode`, `toggleVoucherCode`,
 * `deleteVoucherCode`, `copyVoucherCode`, `validateVoucherCode`,
 * `consumeVoucherCode`, `checkManualBookingVoucher`, plus `startVoucherLiveSync`
 * / `stopVoucherLiveSync` (a 2-second polling loop that re-rendered the whole
 * table; React state removes the need).
 *
 * Vouchers have no backend table — the legacy system kept them on the doctor
 * record in localStorage, and they are still client-side here, stored per doctor.
 * The validation rules that price a booking live in `domain/offers`.
 */

import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Select,
  Tabs,
  DateInput,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import {
  VOUCHER_MAX_USES,
  generateVoucherCode,
  validateVoucher,
  voucherUsesRemaining,
} from '../../domain/offers';
import {
  formatCurrency,
  calculateBookingPrice,
  doctorPriceRange,
  BOOKING_TYPES,
} from '../../domain/pricing';
import { isoToDisplayDate, todayKey } from '../../domain/dates';

const VOUCHER_TYPES = [
  { value: 'single', label: 'استخدام واحد' },
  { value: 'double', label: 'استخدامان' },
  { value: 'permanent', label: 'دائم (بلا حد)' },
];

const TABS = [
  { id: 'active', label: 'أكواد فعّالة', icon: 'ticket' },
  { id: 'used', label: 'مستنفدة', icon: 'ticket-check' },
  { id: 'inactive', label: 'موقوفة', icon: 'ticket-x' },
];

export function DoctorVouchers() {
  const { doctorId, doctorProfile } = useAuth();
  const { success, error: toastError } = useNotifications();

  const [vouchers, setVouchers] = useLocalStorage(
    `capsule_doctor_vouchers_${doctorId ?? 'unknown'}`,
    [],
  );

  const [tab, setTab] = useState('active');
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testCode, setTestCode] = useState('');

  const groups = useMemo(() => {
    const result = { active: [], used: [], inactive: [] };
    for (const voucher of vouchers) {
      if (voucher.active === false) result.inactive.push(voucher);
      else if (voucherUsesRemaining(voucher) <= 0) result.used.push(voucher);
      else result.active.push(voucher);
    }
    return result;
  }, [vouchers]);

  const tabsWithCounts = TABS.map((t) => ({ ...t, count: groups[t.id].length }));
  const list = groups[tab];

  // The doctor's real exam price, not the DoctorProfile column default — see
  // the note in DoctorOffers.jsx. Null when they have no clinic to price from.
  const examplePrice = useMemo(() => {
    const range = doctorPriceRange({ doctor: doctorProfile, bookingType: BOOKING_TYPES.EXAM });
    return range.fromClinic ? range.min : null;
  }, [doctorProfile]);

  const create = (voucher) => {
    if (vouchers.some((v) => v.code === voucher.code)) {
      toastError('هذا الكود موجود بالفعل — جرّب كوداً آخر.');
      return;
    }
    setVouchers([{ ...voucher, usedCount: 0, usedBy: [], createdAt: new Date().toISOString() }, ...vouchers]);
    setCreating(false);
    success(`تم إنشاء الكود ${voucher.code}`);
  };

  const toggle = (code) =>
    setVouchers(vouchers.map((v) => (v.code === code ? { ...v, active: v.active === false } : v)));

  const remove = () => {
    setVouchers(vouchers.filter((v) => v.code !== deleteTarget.code));
    setDeleteTarget(null);
    success('تم حذف الكود');
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      success(`تم نسخ الكود ${code}`);
    } catch {
      toastError('تعذر النسخ — انسخ الكود يدوياً.');
    }
  };

  const testResult = testCode.trim() ? validateVoucher(testCode, vouchers) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="أكواد الخصم"
        subtitle={`${vouchers.length} كود`}
        icon="ticket"
        actions={
          <Button icon="plus" onClick={() => setCreating(true)}>
            إنشاء كود
          </Button>
        }
      />

      {/* Tester — the legacy UI had no way to check a code without booking. */}
      <Panel>
        <Field label="اختبار كود" hint="تحقق من صلاحية كود قبل إعطائه لمريض">
          <div className="flex gap-2">
            <Input
              value={testCode}
              onChange={(event) => setTestCode(event.target.value.toUpperCase())}
              placeholder="أدخل الكود"
              dir="ltr"
              className="flex-1"
            />
            {testCode && (
              <Button variant="ghost" icon="x" onClick={() => setTestCode('')}>
                مسح
              </Button>
            )}
          </div>
        </Field>

        {testResult?.message && (
          <div
            className={`mt-3 flex items-center justify-between gap-3 rounded-xl border p-3 text-xs font-bold ${
              testResult.valid
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                : 'border-red-500/30 bg-red-500/10 text-red-300'
            }`}
          >
            <span>{testResult.message}</span>
            {testResult.valid && (
              <span>
                {formatCurrency(examplePrice)} →{' '}
                {formatCurrency(
                  calculateBookingPrice(examplePrice, { voucher: testResult.percent }).finalPrice,
                )}
              </span>
            )}
          </div>
        )}
      </Panel>

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      {list.length === 0 ? (
        <Panel>
          <EmptyState
            icon="ticket"
            title="لا توجد أكواد هنا"
            message={
              tab === 'active'
                ? 'أنشئ كود خصم لتوزيعه على مرضاك.'
                : tab === 'used'
                  ? 'ستظهر هنا الأكواد التي استُنفد حد استخدامها.'
                  : 'ستظهر هنا الأكواد التي أوقفتها.'
            }
          />
        </Panel>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {list.map((voucher) => (
            <VoucherCard
              key={voucher.code}
              voucher={voucher}
              examplePrice={examplePrice}
              onCopy={() => copy(voucher.code)}
              onToggle={() => toggle(voucher.code)}
              onDelete={() => setDeleteTarget(voucher)}
            />
          ))}
        </div>
      )}

      <CreateVoucherModal
        open={creating}
        examplePrice={examplePrice}
        onClose={() => setCreating(false)}
        onCreate={create}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        destructive
        title="حذف الكود"
        confirmLabel="حذف"
        message={`سيتم حذف الكود ${deleteTarget?.code || ''} نهائياً، ولن يعمل مع أي مريض بعد الآن.`}
      />
    </div>
  );
}

/* ========================================================================== */

function VoucherCard({ voucher, examplePrice, onCopy, onToggle, onDelete }) {
  const remaining = voucherUsesRemaining(voucher);
  const isActive = voucher.active !== false && remaining > 0;
  const discounted = calculateBookingPrice(examplePrice, {
    voucher: voucher.discount ?? voucher.discountPercent ?? 0,
  }).finalPrice;

  return (
    <Panel className={`space-y-3 ${isActive ? '' : 'opacity-60'}`}>
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-purple-500/10 p-2.5 text-purple-400">
          <Icon name="ticket" size={18} />
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate font-mono text-base font-bold tracking-wider text-white" dir="ltr">
            {voucher.code}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
              {voucher.discount ?? voucher.discountPercent}% خصم
            </Badge>
            <Badge className="border-slate-600 bg-slate-800 text-slate-300">
              {remaining === Infinity ? 'بلا حد' : `${remaining} استخدام متبقٍ`}
            </Badge>
            {voucher.expiry && (
              <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                ينتهي {isoToDisplayDate(voucher.expiry) || voucher.expiry}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <p className="rounded-lg bg-white/5 p-2 text-center text-[11px] text-slate-400">
        كشف {formatCurrency(examplePrice)} →{' '}
        <span className="font-bold text-emerald-400">{formatCurrency(discounted)}</span>
      </p>

      {(voucher.usedBy || []).length > 0 && (
        <details className="text-[11px]">
          <summary className="cursor-pointer text-slate-400 hover:text-slate-200">
            استخدمه {voucher.usedBy.length} مريض
          </summary>
          <ul className="mt-2 space-y-1">
            {voucher.usedBy.map((entry, index) => (
              <li key={index} className="flex justify-between text-slate-500">
                <span>{entry.name}</span>
                <span>{isoToDisplayDate(entry.date) || entry.date}</span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex justify-end gap-1.5 border-t border-white/5 pt-3">
        <Button variant="subtle" icon="copy" onClick={onCopy}>
          نسخ
        </Button>
        <Button variant="subtle" icon={isActive ? 'pause' : 'play'} onClick={onToggle}>
          {isActive ? 'إيقاف' : 'تفعيل'}
        </Button>
        <Button variant="subtle" icon="trash-2" onClick={onDelete} className="!text-red-300">
          حذف
        </Button>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function CreateVoucherModal({ open, examplePrice, onClose, onCreate }) {
  const [form, setForm] = useState({
    code: generateVoucherCode(),
    discount: 10,
    type: 'single',
    expiry: '',
    active: true,
  });

  if (!open) return null;

  const preview = calculateBookingPrice(examplePrice, { voucher: form.discount }).finalPrice;

  return (
    <Modal
      open
      onClose={onClose}
      title="إنشاء كود خصم"
      icon="ticket"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            إلغاء
          </Button>
          <Button
            icon="check"
            disabled={!form.code.trim() || !form.discount}
            onClick={() => onCreate({ ...form, code: form.code.trim().toUpperCase() })}
          >
            إنشاء
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="الكود" required>
          <div className="flex gap-2">
            <Input
              value={form.code}
              onChange={(event) => setForm((c) => ({ ...c, code: event.target.value.toUpperCase() }))}
              dir="ltr"
              className="flex-1 font-mono tracking-wider"
            />
            <Button
              variant="ghost"
              icon="dice-5"
              onClick={() => setForm((c) => ({ ...c, code: generateVoucherCode() }))}
            >
              توليد
            </Button>
          </div>
        </Field>

        <Field label="نسبة الخصم (%)" required>
          <Input
            type="number"
            min="1"
            max="100"
            value={form.discount}
            onChange={(event) => setForm((c) => ({ ...c, discount: Number(event.target.value) }))}
          />
        </Field>

        <Field label="حد الاستخدام">
          <Select
            value={form.type}
            onChange={(event) => setForm((c) => ({ ...c, type: event.target.value }))}
          >
            {VOUCHER_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
                {VOUCHER_MAX_USES[option.value] !== Infinity
                  ? ` (${VOUCHER_MAX_USES[option.value]})`
                  : ''}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="تاريخ الانتهاء" hint="اتركه فارغاً لكود بلا تاريخ انتهاء">
          <DateInput
            min={todayKey()}
            value={form.expiry}
            onChange={(event) => setForm((c) => ({ ...c, expiry: event.target.value }))}
          />
        </Field>

        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 text-center">
          <p className="text-[11px] text-slate-400">
            سعر الكشف بعد الخصم (من {formatCurrency(examplePrice)})
          </p>
          <p className="text-lg font-bold text-emerald-400">{formatCurrency(preview)}</p>
        </div>
      </div>
    </Modal>
  );
}

export default DoctorVouchers;
